import React, {useEffect, useRef, useState} from 'react'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CircularProgress from '@mui/material/CircularProgress'
import {toast} from 'react-toastify'

import {useUserContext} from '../../contexts/UserContext'
import {useFetchHook} from '../../hooks/useFetchHook'
import fetcher from '../../fetcher'
import {isSafeLogoUrl} from '../../utils/companyLogo'
import {LogoCropDialog} from './LogoCropDialog'
import {LogoPreviewModal} from './LogoPreviewModal'
import {
    validateLogoFile,
    ACCEPTED_LOGO_EXTENSIONS,
    LOGO_FORMATS_TEXT,
    LOGO_RECOMMENDED_TEXT,
} from './companyLogoValidation'

const IMPORTANT_POINTS = [
    'Use a high resolution, transparent logo',
    'Maximum file size is 2MB',
    'Recommended size is 300x100px (3:1 ratio)',
    'Your logo will appear at the bottom of worksheets and Copy Paste quote cards',
]

// "Add/Replace Your Logo" — the company-logo card on the Profile page. Selecting
// or dropping a PNG/JPEG opens the crop dialog; saving the crop uploads the result
// and immediately updates the branding used by worksheets and copy/paste. If a
// logo already exists, uploading a new one replaces it (no separate Replace
// button). Preview opens a read-only Logo Preview modal; Remove clears the logo
// after an inline confirmation.
export const CompanyLogoSettings = () => {
    const {profileState, setProfileDealStateWithoutUpdate} = useUserContext()
    const [uploadResponse, uploadLogo] = useFetchHook(fetcher.uploadAccountLogo)
    const [, removeLogo] = useFetchHook(fetcher.removeAccountLogo)

    const currentLogoUrl = profileState?.account_logo_url
    const hasLogo = isSafeLogoUrl(currentLogoUrl)
    const companyName = profileState?.account_name

    const fileInputRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)
    const [error, setError] = useState(null)
    const [confirmingRemove, setConfirmingRemove] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    // The freshly-selected file, held for the crop dialog. objectUrl is revoked
    // when the dialog closes or the component unmounts.
    const [selected, setSelected] = useState(null) // { objectUrl }

    const isSaving = uploadResponse?.loading

    useEffect(() => {
        return () => {
            if (selected?.objectUrl) URL.revokeObjectURL(selected.objectUrl)
        }
    }, [selected])

    const openFileDialog = () => fileInputRef.current?.click()

    const handleFile = (file) => {
        setError(null)
        const validationError = validateLogoFile(file)
        if (validationError) {
            setError(validationError)
            return
        }
        setSelected({objectUrl: URL.createObjectURL(file)})
    }

    const handleInputChange = (event) => {
        const file = event.target.files?.[0]
        if (file) handleFile(file)
        event.target.value = '' // allow re-picking the same file
    }

    const handleDrop = (event) => {
        event.preventDefault()
        setIsDragging(false)
        const file = event.dataTransfer.files?.[0]
        if (file) handleFile(file)
    }

    const closeCropDialog = () => {
        if (selected?.objectUrl) URL.revokeObjectURL(selected.objectUrl)
        setSelected(null)
    }

    const handleCropSave = (blob) => {
        // The crop always exports PNG (lossless, transparent), so the upload is
        // always a .png regardless of the source format.
        const formData = new FormData()
        formData.append('file', blob, 'logo.png')

        uploadLogo(formData, {headers: {'Content-Type': 'multipart/form-data'}})
            .then((response) => {
                const nextUrl = response?.data?.account_logo_url
                setProfileDealStateWithoutUpdate({account_logo_url: nextUrl})
                toast.success('Logo updated.')
                closeCropDialog()
            })
            .catch((err) => {
                // Keep the current logo intact on failure; surface the message.
                const message = err?.response?.data?.error || 'Could not upload the logo. Please try again.'
                setError(message)
                closeCropDialog()
            })
    }

    const handleRemove = () => {
        setIsRemoving(true)
        setError(null)
        removeLogo()
            .then((response) => {
                const nextUrl = response?.data?.account_logo_url ?? ''
                setProfileDealStateWithoutUpdate({account_logo_url: nextUrl})
                toast.success('Logo removed.')
                setConfirmingRemove(false)
            })
            .catch(() => {
                setError('Could not remove the logo. Please try again.')
            })
            .finally(() => setIsRemoving(false))
    }

    return (
        <div className={'profile-panel CompanyLogoSettings'}>
            <div className={'panel-header'}>
                <div className={'panel-title'}>
                    <CloudUploadOutlinedIcon/>
                    Add/Replace Your Logo
                </div>
            </div>

            <div className={'panel-body'}>
                <p className={'CompanyLogoSettings__intro'}>
                    Upload your logo to have it displayed on Worksheets and Copy Paste features.
                </p>

                <div className={'CompanyLogoSettings__top'}>
                    {/* Drop zone */}
                    <div
                        className={'CompanyLogoSettings__dropzone' + (isDragging ? ' is-dragging' : '')}
                        role={'button'}
                        tabIndex={0}
                        aria-label={hasLogo ? 'Replace your logo' : 'Add your logo'}
                        onClick={openFileDialog}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                openFileDialog()
                            }
                        }}
                        onDragOver={(e) => {
                            e.preventDefault()
                            setIsDragging(true)
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <span className={'CompanyLogoSettings__dropzone-icon'}><CloudUploadOutlinedIcon/></span>
                        <span className={'CompanyLogoSettings__dropzone-primary'}>Drag and drop your image here</span>
                        <span className={'CompanyLogoSettings__dropzone-or'}>or</span>
                        <button
                            type={'button'}
                            className={'CompanyLogoSettings__select-btn'}
                            onClick={(e) => {
                                // Stop the dropzone's own onClick from firing a second picker.
                                e.stopPropagation()
                                openFileDialog()
                            }}
                        >
                            Select Image
                        </button>
                        <span className={'CompanyLogoSettings__hint'}>{LOGO_FORMATS_TEXT}</span>
                        <span className={'CompanyLogoSettings__hint'}>{LOGO_RECOMMENDED_TEXT}</span>

                        <input
                            ref={fileInputRef}
                            type={'file'}
                            accept={ACCEPTED_LOGO_EXTENSIONS}
                            onChange={handleInputChange}
                            style={{display: 'none'}}
                            aria-hidden={'true'}
                            tabIndex={-1}
                        />
                    </div>

                    {/* IMPORTANT guidance */}
                    <aside className={'CompanyLogoSettings__important'}>
                        <div className={'important-header'}>
                            <span className={'important-title'}>Important</span>
                            <InfoOutlinedIcon/>
                        </div>
                        <ul>
                            {IMPORTANT_POINTS.map((point) => (
                                <li key={point}>
                                    <CheckCircleOutlineIcon/>
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>
                </div>

                {error && <div className={'CompanyLogoSettings__error'} role={'alert'}>{error}</div>}

                <hr className={'CompanyLogoSettings__divider'}/>

                {/* Current Logo section */}
                <div className={'CompanyLogoSettings__current-header'}>
                    <span className={'current-label'}>Current Logo</span>
                    <InfoOutlinedIcon/>
                </div>

                <div className={'CompanyLogoSettings__current-row'}>
                    {hasLogo ? (
                        <>
                            <div className={'CompanyLogoSettings__current-frame'}>
                                <img src={currentLogoUrl} alt={`${companyName || 'Company'} logo`}/>
                            </div>
                            <div className={'CompanyLogoSettings__current-actions'}>
                                <button
                                    type={'button'}
                                    className={'CompanyLogoSettings__action-btn'}
                                    onClick={() => setIsPreviewOpen(true)}
                                >
                                    <VisibilityOutlinedIcon/>
                                    Preview Logo
                                </button>
                                <button
                                    type={'button'}
                                    className={'CompanyLogoSettings__action-btn CompanyLogoSettings__action-btn--danger'}
                                    onClick={() => setConfirmingRemove(true)}
                                >
                                    <DeleteOutlineIcon/>
                                    Remove Logo
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className={'CompanyLogoSettings__current-empty'}>
                            No logo uploaded yet. Worksheets and Copy Paste features will display your company name.
                        </div>
                    )}
                </div>

                <p className={'CompanyLogoSettings__current-hint'}>
                    This logo will appear at the bottom of worksheets and Copy Paste quote cards.
                </p>

                {confirmingRemove && (
                    <div className={'CompanyLogoSettings__confirm'} role={'group'} aria-label={'Confirm logo removal'}>
                        <span className={'confirm-text'}>
                            Remove company logo? Worksheets and Copy Paste features will return to displaying your company name.
                        </span>
                        <button
                            type={'button'}
                            className={'CompanyLogoSettings__confirm-btn'}
                            onClick={() => setConfirmingRemove(false)}
                            disabled={isRemoving}
                        >
                            Cancel
                        </button>
                        <button
                            type={'button'}
                            className={'CompanyLogoSettings__confirm-btn CompanyLogoSettings__confirm-btn--danger'}
                            onClick={handleRemove}
                            disabled={isRemoving}
                        >
                            {isRemoving ? <CircularProgress size={14} color={'inherit'}/> : 'Remove Logo'}
                        </button>
                    </div>
                )}
            </div>

            {selected && (
                <LogoCropDialog
                    imageSrc={selected.objectUrl}
                    isSaving={isSaving}
                    onCancel={closeCropDialog}
                    onSave={handleCropSave}
                />
            )}

            {isPreviewOpen && (
                <LogoPreviewModal
                    logoUrl={currentLogoUrl}
                    companyName={companyName}
                    onClose={() => setIsPreviewOpen(false)}
                />
            )}
        </div>
    )
}
